import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpErrorResponse, httpResource } from '@angular/common/http';
import { OrganizationLicenceService } from '../../Modules/Organizations/Services/organization-licence.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { ErrorHandlerService } from '../../Modules/Shared/Services/error-handler.service';
import { MsgService } from '../../Modules/Shared/Services/msg.service';
import { TranslateService } from '@ngx-translate/core';
import { OperatorFacade } from '../operator.facade';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE, VALUE_UNRESOLVED } from '../../Consts/backoffice.const';
import { ROLE_BILLING } from '../../Consts/organization-detail.const';
import {
  ADJUST_APPLIED_MESSAGE,
  LICENCE_CANCELLED_MESSAGE,
  LICENCE_HELD_BY_KEY,
  LICENCE_RESUMED_MESSAGE,
  RESUME_APPLIED_MESSAGE,
} from '../../Consts/licences.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { Licence } from '../../Models/backoffice/licence.model';
import { LicenceComponent } from '../../Models/backoffice/licence-component.model';
import {
  LicenceAdjustPayload,
  LicenceFilters,
  LicenceResumePayload,
} from '../../Modules/Organizations/Interfaces/licence-filters.interface';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import {
  LicenceConflictEnvelope,
  LicencePortfolioResponse,
} from '../../Interfaces/api/backoffice/licence.interface';

@Injectable()
export class OrganizationLicenceFacade {
  private readonly _licenceService = inject(OrganizationLicenceService);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _errorHandler = inject(ErrorHandlerService);
  private readonly _translate = inject(TranslateService);
  private readonly _msgService = inject(MsgService);
  private readonly _operatorFacade = inject(OperatorFacade);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _organizationId = signal<string>('');
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _isMutating = signal<boolean>(false);

  private readonly _filters = signal<LicenceFilters>({
    component_type: '',
    holder: '',
    state: '',
  });

  private readonly _portfolio = httpResource<LaravelDefaultResponse<LicencePortfolioResponse>>(() =>
    this._organizationId()
      ? this._licenceService.portfolioRequest(
          this._organizationId(),
          this._filters(),
          this._page(),
          this._pageSize(),
        )
      : undefined,
  );

  readonly filters = computed<LicenceFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly isMutating = computed<boolean>(() => this._isMutating());

  readonly licences = computed<Licence[]>(() =>
    this._licenceService.toLicences(this._resourceState.valueOf(this._portfolio)),
  );

  readonly components = computed<LicenceComponent[]>(() =>
    this._licenceService.toComponents(this._resourceState.valueOf(this._portfolio)),
  );

  readonly total = computed<number>(() => this._licenceService.toTotal(this._resourceState.valueOf(this._portfolio)));

  readonly halfStateTotal = computed<number>(() =>
    this._licenceService.toHalfStateTotal(this._resourceState.valueOf(this._portfolio)),
  );

  readonly subscriptionSummary = computed<LicencePortfolioResponse['subscription']>(() =>
    this._licenceService.toSubscriptionSummary(this._resourceState.valueOf(this._portfolio)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._portfolio.status()),
  );

  readonly hasSubscription = computed<boolean>(() => this.subscriptionSummary() !== null);

  readonly isAdjustable = computed<boolean>(
    () => this.subscriptionSummary()?.is_adjustable ?? false,
  );

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  readonly canAdjust = computed<boolean>(() => this._operatorFacade.atLeast(ROLE_BILLING));

  readonly hasDrift = computed<boolean>(() =>
    this.components().some((component) => component.isDrifted),
  );

  load(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  applyFilters(filters: LicenceFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({ component_type: '', holder: '', state: '' });
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._portfolio.reload();
  }

  adjust(payload: LicenceAdjustPayload): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._licenceService
      .adjust(organizationId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(ADJUST_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  resumeQuantity(payload: LicenceResumePayload): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._licenceService
      .resumeQuantity(organizationId, payload)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(RESUME_APPLIED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  cancelOne(licenceId: string, reason: string): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._licenceService
      .cancelOne(organizationId, licenceId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(LICENCE_CANCELLED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  resumeOne(licenceId: string, reason: string): void {
    const organizationId = this._organizationId();

    if (!organizationId) {
      return;
    }

    this._isMutating.set(true);

    this._licenceService
      .resumeOne(organizationId, licenceId, reason)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: () => this._afterWrite(LICENCE_RESUMED_MESSAGE),
        error: (error) => this._failMutation(error),
      });
  }

  private _afterWrite(message: string): void {
    this._isMutating.set(false);
    this._portfolio.reload();
    this._msgService.showSnackBar(message, { type: 'success' });
  }

  private _failMutation(error: unknown): void {
    this._isMutating.set(false);
    this._msgService.showSnackBar(this._conflictMessage(error), { type: 'error' });
  }

  private _conflictMessage(error: unknown): string {
    const base = this._translate.instant(this._errorHandler.messageFor(error));
    const holder = this._heldBy(error);

    return holder === null ? base : this._translate.instant(LICENCE_HELD_BY_KEY, { base, holder });
  }

  private _heldBy(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const envelope = error.error as LicenceConflictEnvelope | null;
    const held = envelope?.data?.held_by;

    if (!held) {
      return null;
    }

    return held.holder_name || held.holder_email || held.holder_id || VALUE_UNRESOLVED;
  }
}
